import { useTodoDrawerStore } from '@/stores/todoDrawerStore';
import { Button, Menu, Text } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { IconChevronDown } from '@tabler/icons-react';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { CreateNoteInput } from '../services/noteServices';
import { useCreateNote } from '../services/useNoteQueries';

interface CreateNoteButtonProps {
    goalId?: string;
    todoId?: string;
    children?: React.ReactNode;
}

const CreateNoteButton: React.FC<CreateNoteButtonProps> = ({
    goalId,
    todoId,
    children,
}) => {
    const createNoteMutation = useCreateNote();
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { isDrawerOpen, closeDrawer } = useTodoDrawerStore();
    const { t } = useTranslation('note');
    // 현재 노트의 임시 ID를 저장할 ref
    const tempNoteIdRef = useRef<string | null>(null);

    // 마우스가 버튼 위에 올라갔을 때 필요한 리소스 미리 로드
    const handleMouseEnter = () => {
        // NotePage 컴포넌트 미리 로드
        void import('../pages/NotePage').catch(error => {
            console.error('Failed to preload NotePage:', error);
        });

        // Editor 컴포넌트 미리 로드
        void import('../components/Editor').catch(error => {
            console.error('Failed to preload Editor:', error);
        });
    };

    const handleCreateNote = async ({
        isNewWindow,
    }: {
        isNewWindow: boolean;
    }) => {
        if (!user) {
            Notifications.show({
                title: t('auth.required'),
                message: t('auth.login_required'),
                color: 'red',
            });
            return;
        }

        // goalId 또는 todoId 중 하나는 필요합니다
        if (!goalId && !todoId) {
            Notifications.show({
                title: t('info.required'),
                message: t('info.id_required'),
                color: 'red',
            });
            return;
        }

        try {
            // 노트 생성 데이터 준비
            const noteData: CreateNoteInput = {
                title: '',
                register_id: user.id,
            };

            // goalId가 있으면 추가
            if (goalId) {
                noteData.goal_id = goalId;
            }

            // todoId가 있으면 추가
            if (todoId) {
                noteData.todo_id = todoId;
            }

            // Optimistic Update 사용 (임시 ID 활용)
            const tempId = 'temp_' + Date.now();
            tempNoteIdRef.current = tempId;

            if (isDrawerOpen) {
                closeDrawer();
            }

            if (isNewWindow) {
                const newNote = await createNoteMutation.mutateAsync(noteData);
                window.open(`/note/${newNote.id}`, '_blank');
            } else {
                // 즉시 페이지 이동 (API 호출 결과를 기다리지 않음)
                void navigate(`/note/${tempId}`);

                // 백그라운드에서 실제 API 호출 실행
                const newNote = await createNoteMutation.mutateAsync(noteData);

                // API 호출 성공 후 URL 업데이트 (임시 ID에서 실제 ID로)
                if (tempNoteIdRef.current === tempId) {
                    // 실제 ID로 URL 업데이트 (replace로 브라우저 히스토리 교체)
                    void navigate(`/note/${newNote.id}`, { replace: true });
                    tempNoteIdRef.current = null;
                }
            }

            // 성공 알림 표시
            Notifications.show({
                title: t('create.success'),
                message: t('create.success_message'),
                color: 'green',
            });
        } catch (error) {
            console.error('Failed to create note:', error);

            // 실패 시 목록 페이지로 리다이렉트 (임시 노트 페이지에 있다면)
            if (tempNoteIdRef.current) {
                void navigate(-1); // 이전 페이지로 이동
                tempNoteIdRef.current = null;
            }

            Notifications.show({
                title: t('create.fail'),
                message: t('create.fail_message'),
                color: 'red',
            });
        }
    };

    return (
        <Button.Group>
            <Button
                onClick={e => {
                    e.preventDefault();
                    handleCreateNote({ isNewWindow: false }).catch(error => {
                        console.error('Error in handleCreateNote:', error);
                    });
                }}
                onMouseEnter={handleMouseEnter} // 마우스가 버튼 위에 올라갔을 때 리소스 미리 로드
                loading={createNoteMutation.isPending}
                variant="default"
                radius="md"
                size="xs"
                color="gray"
            >
                <Text size="xs">{t('create.title')}</Text>
            </Button>
            {/* 플래너 - 팝오버 열었을 때 클릭 감지 가능하게 추가, 다른 데서 문제 발생 시 해당 케이스만 withinPortal false로 수정할 것 */}
            <Menu withinPortal={false} radius="md">
                <Menu.Target>
                    <Button
                        variant="default"
                        size="xs"
                        color="gray"
                        radius="md"
                        styles={{ root: { padding: '0 8px' } }}
                        onMouseEnter={handleMouseEnter}
                        loading={createNoteMutation.isPending}
                    >
                        <IconChevronDown size={12} stroke={2.5} />
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCreateNote({ isNewWindow: true }).catch(
                                error => {
                                    console.error(
                                        'Error in handleCreateNote:',
                                        error
                                    );
                                }
                            );
                        }}
                    >
                        <Text size="xs">{t('create.in_new_window')}</Text>
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </Button.Group>
    );
};

export default CreateNoteButton;
